import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"./ui/select";
import { Calculator, ArrowRight } from "lucide-react";

const boatSizes = [
{ value: "small", label: "Under 20 ft", multiplier: 1 },
{ value: "medium", label: "20-25 ft", multiplier: 1.25 },
{ value: "large", label: "25-30 ft", multiplier: 1.5 }];


const services = [
{ id: "exterior", name: "Exterior Wash", basePrice: 100, description: "Hull & deck cleaning" },
{ id: "buffing", name: "Buffing & Polishing", basePrice: 275, description: "Restore shine" },
{ id: "waxing", name: "Waxing & Protection", basePrice: 275, description: "UV protection" },
{ id: "interior", name: "Interior Detailing", basePrice: 150, description: "interior surfaces & upholstery" },
{ id: "mold", name: "Mold & Mildew Removal", basePrice: 100, description: "Deep sanitization" }];


const packages = [
{
  id: "basic",
  name: "Basic Wash",
  services: ["exterior"],
  discount: 0,
  popular: false
},
{
  id: "standard",
  name: "Full Detail",
  services: ["exterior", "buffing", "waxing", "interior"],
  discount: 15,
  popular: true
},
{
  id: "premium",
  name: "Complete Care",
  services: ["exterior", "buffing", "waxing", "interior", "mold"],
  discount: 20,
  popular: false
}];


export default function PricingCalculator() {
  const [boatSize, setBoatSize] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [activePackage, setActivePackage] = useState(null);

  const toggleService = (serviceId) => {
    setActivePackage(null);
    setSelectedServices((prev) =>
    prev.includes(serviceId) ?
    prev.filter((id) => id !== serviceId) :
    [...prev, serviceId]
    );
  };

  const selectPackage = (pkg) => {
    setActivePackage(pkg.id);
    setSelectedServices(pkg.services);
  };

  const calculation = useMemo(() => {
    if (!boatSize || selectedServices.length === 0) {
      return { subtotal: 0, discount: 0, total: 0 };
    }

    const sizeMultiplier = boatSizes.find((s) => s.value === boatSize)?.multiplier || 1;

    const subtotal = selectedServices.reduce((sum, serviceId) => {
      const service = services.find((s) => s.id === serviceId);
      return sum + (service?.basePrice || 0) * sizeMultiplier;
    }, 0);

    const pkg = packages.find((p) => p.id === activePackage);
    const discountPercent = pkg?.discount || 0;
    const discount = subtotal * (discountPercent / 100);
    const total = subtotal - discount;

    return { subtotal, discount, discountPercent, total };
  }, [boatSize, selectedServices, activePackage]);

  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="pricing"
      className="section-padding bg-white"
      data-testid="pricing-section">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span
            className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-[#64748B] mb-4"
            data-testid="pricing-label">

            Transparent Pricing
          </span>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1E36] tracking-tight mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="pricing-heading">

            Estimate Your Detail
          </h2>
          <p className="text-[#64748B] max-w-2xl mx-auto">
            Get an instant estimate by selecting your boat size and services. Prices may vary based on condition.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Package Selection */}
          <div className="lg:col-span-1 space-y-4" data-testid="packages-container">
            <h3
              className="text-lg font-semibold text-[#0B1E36] mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}>

              Quick Packages
            </h3>
            {packages.map((pkg) =>
            <button
              key={pkg.id}
              onClick={() => selectPackage(pkg)}
              className={`w-full text-left p-4 border transition-all duration-300 relative ${
              activePackage === pkg.id ?
              "border-[#FACC15] bg-[#FACC15]/10" :
              "border-[#E2E8F0] hover:border-[#0B1E36]/30"}`
              }
              data-testid={`package-${pkg.id}`}>

                {pkg.popular &&
              <span className="absolute -top-3 right-4 bg-[#FACC15] text-[#0B1E36] text-xs font-bold px-2 py-1">
                    POPULAR
                  </span>
              }
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-semibold text-[#0B1E36]">{pkg.name}</h4>
                    <p className="text-sm text-[#64748B] mt-1">
                      {pkg.services.length} services included
                    </p>
                  </div>
                  {pkg.discount > 0 &&
                <span className="text-sm font-semibold text-green-600">
                      {pkg.discount}% OFF
                    </span>
                }
                </div>
              </button>
            )}
          </div>

          {/* Middle: Service Selection */}
          <div className="lg:col-span-1" data-testid="services-selection">
            <h3
              className="text-lg font-semibold text-[#0B1E36] mb-4"
              style={{ fontFamily: "'Outfit', sans-serif" }}>

              Or Build Your Own
            </h3>
            
            {/* Boat Size Selector */}
            <div className="mb-6">
              <Label className="text-[#0F172A] font-medium mb-2 block">
                Boat Size
              </Label>
              <Select value={boatSize} onValueChange={setBoatSize}>
                <SelectTrigger
                  className="rounded-none border-[#E2E8F0]"
                  data-testid="boat-size-select">

                  <SelectValue placeholder="Select boat size" />
                </SelectTrigger>
                <SelectContent>
                  {boatSizes.map((size) =>
                  <SelectItem
                    key={size.value}
                    value={size.value}
                    data-testid={`size-${size.value}`}>

                      {size.label}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Services Checklist */}
            <div className="space-y-3">
              {services.map((service) =>
              <div
                key={service.id}
                className={`flex items-start gap-3 p-3 border transition-all duration-300 cursor-pointer ${
                selectedServices.includes(service.id) ?
                "border-[#0B1E36] bg-[#F8FAFC]" :
                "border-[#E2E8F0]"}`
                }
                onClick={() => toggleService(service.id)}
                data-testid={`service-checkbox-${service.id}`}>

                  <Checkbox
                  id={service.id}
                  checked={selectedServices.includes(service.id)}
                  onCheckedChange={() => toggleService(service.id)}
                  className="mt-0.5" />

                  <div className="flex-1">
                    <Label
                    htmlFor={service.id}
                    className="text-[#0F172A] font-medium cursor-pointer">

                      {service.name}
                    </Label>
                    <p className="text-xs text-[#64748B]">{service.description}</p>
                  </div>
                  <span className="text-sm text-[#64748B]">
                    from ${service.basePrice}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Price Summary */}
          <div className="lg:col-span-1" data-testid="price-summary">
            <div className="bg-[#0B1E36] p-6 lg:p-8 text-white sticky top-24">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#FACC15] flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-[#0B1E36]" />
                </div>
                <h3
                  className="text-lg font-semibold"
                  style={{ fontFamily: "'Outfit', sans-serif" }}>

                  Your Estimate
                </h3>
              </div>

              {!boatSize ?
              <p className="text-white/60 text-sm mb-6">
                  Select a boat size to see pricing
                </p> :
              selectedServices.length === 0 ?
              <p className="text-white/60 text-sm mb-6">
                  Select services to calculate
                </p> :

              <div className="space-y-4 mb-6">
                  {/* Selected Services */}
                  <div className="border-b border-white/20 pb-4">
                    {selectedServices.map((serviceId) => {
                    const service = services.find((s) => s.id === serviceId);
                    const sizeMultiplier = boatSizes.find((s) => s.value === boatSize)?.multiplier || 1;
                    const price = (service?.basePrice || 0) * sizeMultiplier;
                    return (
                      <div key={serviceId} className="flex justify-between text-sm py-1">
                          <span className="text-white/80">{service?.name}</span>
                          <span>${price.toFixed(0)}</span>
                        </div>);

                  })}
                  </div>

                  {/* Subtotal */}
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Subtotal</span>
                    <span>${calculation.subtotal.toFixed(0)}</span>
                  </div>

                  {/* Discount */}
                  {calculation.discount > 0 &&
                <div className="flex justify-between text-sm text-green-400">
                      <span>Package Discount ({calculation.discountPercent}%)</span>
                      <span>-${calculation.discount.toFixed(0)}</span>
                    </div>
                }

                  {/* Total */}
                  <div className="flex justify-between text-xl font-bold pt-4 border-t border-white/20">
                    <span>Estimated Total</span>
                    <span className="text-[#FACC15]" data-testid="total-price">
                      ${calculation.total.toFixed(0)}
                    </span>
                  </div>
                </div>
              }

              <Button
                onClick={scrollToContact}
                className="w-full bg-[#FACC15] text-[#0B1E36] hover:bg-[#EAB308] font-semibold py-6 rounded-none transition-all duration-300"
                data-testid="get-quote-from-calculator">

                Get Exact Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-xs text-white/50 mt-4 text-center">
                *Final pricing based on boat condition
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}